"use client";

import { quizLinkLabel } from "@/lib/i18n/quiz-link-language";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Award,
  BookOpen,
  CheckCircle,
  Clock,
  RotateCcw,
  X,
  XCircle,
} from "lucide-react";

import { loadQuizData, type QuizId } from "./registry";
import type { McqQuestion, QuizData } from "./types";
import type { Locale } from "@/lib/i18n/locales";
import { quizRunnerCopy, formatQuizRunner } from "@/lib/i18n/quiz-runner-copy";
import { renderInlineMath } from "./render-math";
import { getQuizOptionOrder } from "./option-order";
import ShareQuizResult from "./ShareQuizResult";
import QuizWhatsAppCta from "./QuizWhatsAppCta";
import {
  createQuizAttemptId,
  trackQuizCtaView,
  trackQuizEvent,
  trackQuizNotesReviewClick,
  type QuizLoadFailureReason,
  type QuizStudyContext,
} from "@/lib/quiz-analytics";
import { BUS_QUIZ_STORAGE_VERSION, quizStorageKeys } from "./bus-quiz-storage";

type AnswerMap = Record<number, number | undefined>;

type StoredProgress = {
  version: number;
  revision?: string;
  persistenceRevision?: string;
  currentIndex: number;
  totalQuestions?: number;
  answers: Record<number, number>;
  checkedIds: number[];
  /** Number of correct answers so far - added 2026-03-23 for real score persistence. */
  correctCount?: number;
};

type BusQuizRunnerProps = {
  locale?: Locale;
  quizId: QuizId;
  initialQuizData?: QuizData;
  notesHref: string | null;
  /** Use a concise label when the bank title already contains notes/quiz wording. */
  notesLabel?: string;
  hubHref: string;
  /** Override both return labels when the destination is not a quiz topic hub. */
  returnLabel?: string;
  /** When set, enables timed exam mode with a countdown. */
  timedSeconds?: number;
  /** Start with the locally stored questions the student previously missed. */
  reviewMissed?: boolean;
  studyContext?: QuizStudyContext | null;
};

type BusQuizMode = "full" | "fix-it";

const shuffleQuestions = (questions: McqQuestion[]) => {
  const shuffled = [...questions];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function BusQuizRunner(props: BusQuizRunnerProps) {
  // Remount before effects can save the previous session into a new language/version.
  const identity = `${props.locale ?? "en"}:${props.quizId}:${
    props.initialQuizData?.meta.persistenceRevision ??
    props.initialQuizData?.meta.revision ??
    ""
  }`;
  return <BusQuizSession key={identity} {...props} />;
}

function BusQuizSession({
  locale = "en",
  quizId,
  initialQuizData,
  notesHref,
  notesLabel: notesLabelOverride,
  hubHref,
  returnLabel,
  timedSeconds,
  reviewMissed = false,
  studyContext = null,
}: BusQuizRunnerProps) {
  const copy = useMemo(() => quizRunnerCopy(locale), [locale]);
  const [quizData, setQuizData] = useState<QuizData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<AnswerMap>({});
  const [checkedIds, setCheckedIds] = useState<Record<number, boolean>>({});
  const [isComplete, setIsComplete] = useState(false);
  const [hasRestored, setHasRestored] = useState(false);
  const [mode, setMode] = useState<BusQuizMode>("full");
  const [fixItQuestions, setFixItQuestions] = useState<McqQuestion[]>([]);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(
    timedSeconds ?? null
  );
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoCompleteRef = useRef(false);

  // Abandonment tracking refs - readable in cleanup without stale closures
  const startedRef = useRef(false);
  const completedRef = useRef(false);
  const abandonedRef = useRef(false);
  const viewedQuizIdRef = useRef<string | null>(null);
  const attemptIdRef = useRef(createQuizAttemptId());
  const attemptStartedAtRef = useRef<number | null>(null);
  // Guards the one-per-results-appearance quiz_cta_view impression event.
  const ctaViewFiredRef = useRef(false);
  const desktopFeedbackRef = useRef<HTMLDivElement>(null);
  const mobileFeedbackRef = useRef<HTMLDivElement>(null);
  const feedbackViewedKeysRef = useRef(new Set<string>());
  const trackedLoadFailuresRef = useRef(new Set<string>());
  const checkedIdsRef = useRef(checkedIds);
  checkedIdsRef.current = checkedIds;
  // mcqQuestionsRef is populated below after useMemo
  const mcqQuestionsRef = useRef<McqQuestion[]>([]);
  const quizIdRef = useRef(quizId);
  quizIdRef.current = quizId;

  const {
    progress: storageKey,
    completion: completionKey,
    score: scoreKey,
    mistakes: mistakesKey,
  } = quizStorageKeys(quizId, locale, quizData?.meta.persistenceRevision);
  const studySubject = studyContext?.studySubject;
  const studyTopic = studyContext?.studyTopic;

  const reportLoadFailure = useCallback(
    (failureReason: QuizLoadFailureReason) => {
      const failureKey = `${quizId}:${failureReason}`;
      if (trackedLoadFailuresRef.current.has(failureKey)) return;
      trackedLoadFailuresRef.current.add(failureKey);
      trackQuizEvent("quiz_load_failed", {
        quizId,
        attemptId: attemptIdRef.current,
        surface: "standalone",
        mode: "full",
        failure_reason: failureReason,
        ...(studySubject && { studySubject }),
        ...(studyTopic && { studyTopic }),
      });
    },
    [quizId, studySubject, studyTopic]
  );

  // Timer countdown
  useEffect(() => {
    if (timedSeconds == null || isComplete || isLoading || !quizData) return;

    setTimeRemaining(timedSeconds);
    autoCompleteRef.current = false;

    timerRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev === null || prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          autoCompleteRef.current = true;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [timedSeconds, isComplete, isLoading, quizData]);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);
    setQuizData(null);
    setCurrentIndex(0);
    setAnswers({});
    setCheckedIds({});
    setIsComplete(false);
    setHasRestored(false);
    setMode("full");
    setFixItQuestions([]);

    const quizPromise = initialQuizData
      ? Promise.resolve(initialQuizData)
      : loadQuizData(quizId);

    quizPromise
      .then((data) => {
        if (cancelled) return;
        if (data && (data.mcqQuestions?.length ?? 0) > 0) {
          setQuizData(data);
        } else if (data) {
          setQuizData(data);
          reportLoadFailure("empty_question_bank");
        } else {
          setLoadError(formatQuizRunner(copy.notFound, { quizId }));
          reportLoadFailure("not_found");
        }
      })
      .catch((error) => {
        if (cancelled) return;
        if (process.env.NODE_ENV !== "production") {
          console.error(`Failed to load quiz data for ${quizId}:`, error);
        }
        setLoadError(copy.loadError);
        reportLoadFailure("load_error");
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [copy, initialQuizData, quizId, reportLoadFailure]);

  const mcqQuestions: McqQuestion[] = useMemo(
    () => quizData?.mcqQuestions ?? [],
    [quizData]
  );
  // Keep live ref in sync for use inside effect cleanups
  mcqQuestionsRef.current = mcqQuestions;

  const activeQuestions =
    mode === "fix-it" && fixItQuestions.length > 0
      ? fixItQuestions
      : mcqQuestions;

  const questionIds = useMemo(
    () => new Set(mcqQuestions.map((question) => question.id)),
    [mcqQuestions]
  );

  // Auto-complete when time runs out
  const handleTimedAutoComplete = useCallback(() => {
    if (!autoCompleteRef.current) return;
    autoCompleteRef.current = false;
    if (timerRef.current) clearInterval(timerRef.current);
    setIsComplete(true);
    try {
      window.localStorage.setItem(completionKey, new Date().toISOString());
      const finalScore = mcqQuestions.reduce(
        (c, q) => (q.correct === answers[q.id] ? c + 1 : c),
        0
      );
      window.localStorage.setItem(
        scoreKey,
        JSON.stringify({ score: finalScore, total: mcqQuestions.length })
      );
    } catch {
      // storage may be full
    }
  }, [answers, mcqQuestions, completionKey, scoreKey]);

  useEffect(() => {
    if (timeRemaining === 0 && !isComplete) {
      handleTimedAutoComplete();
    }
  }, [timeRemaining, isComplete, handleTimedAutoComplete]);

  const analyticsContext = useCallback(
    () => ({
      quizId: quizIdRef.current,
      attemptId: attemptIdRef.current,
      surface: "standalone" as const,
      mode,
      ...studyContext,
    }),
    [mode, studyContext]
  );

  const markStarted = useCallback(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    attemptStartedAtRef.current = Date.now();
    trackQuizEvent("quiz_started", {
      ...analyticsContext(),
      total: mcqQuestionsRef.current.length,
    });
  }, [analyticsContext]);

  // A loaded quiz is an impression. An attempt starts only after an answer.
  useEffect(() => {
    if (!quizData || mcqQuestions.length === 0) return;

    // Reset flags when a new quiz loads
    startedRef.current = false;
    completedRef.current = false;
    abandonedRef.current = false;
    attemptStartedAtRef.current = null;

    if (viewedQuizIdRef.current !== quizId) {
      viewedQuizIdRef.current = quizId;
      trackQuizEvent("quiz_viewed", {
        quizId,
        attemptId: attemptIdRef.current,
        surface: "standalone",
        mode,
        total: mcqQuestions.length,
        ...studyContext,
      });
    }

    const fireAbandoned = () => {
      if (!startedRef.current || completedRef.current || abandonedRef.current)
        return;
      abandonedRef.current = true;
      trackQuizEvent("quiz_abandoned", {
        ...analyticsContext(),
        questions_answered: Object.keys(checkedIdsRef.current).length,
        total: mcqQuestionsRef.current.length,
        elapsed_seconds: attemptStartedAtRef.current
          ? Math.round((Date.now() - attemptStartedAtRef.current) / 1000)
          : undefined,
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        fireAbandoned();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      fireAbandoned();
    };
  }, [
    analyticsContext,
    quizData,
    mcqQuestions.length,
    mode,
    quizId,
    studyContext,
  ]);

  // Mark completion so the abandoned-cleanup guard is lifted
  useEffect(() => {
    if (isComplete) {
      completedRef.current = true;
    }
  }, [isComplete]);

  // Fire a single quiz_cta_view impression when the results screen (which
  // always renders the WhatsApp CTA here) first appears, so quiz_whatsapp_lead
  // clicks have a denominator. Ref-guarded rather than fired in the render body
  // to avoid re-firing on every results re-render; reset when the screen leaves
  // so a fix-it re-completion counts as a fresh impression.
  useEffect(() => {
    if (!isComplete) {
      ctaViewFiredRef.current = false;
      return;
    }
    if (ctaViewFiredRef.current) return;
    ctaViewFiredRef.current = true;
    const score = activeQuestions.reduce(
      (correct, q) => (q.correct === answers[q.id] ? correct + 1 : correct),
      0
    );
    trackQuizCtaView({
      quizId,
      cta: "whatsapp",
      score,
      total: activeQuestions.length,
      studyContext,
    });
  }, [isComplete, activeQuestions, answers, quizId, studyContext]);

  useEffect(() => {
    if (!quizData || hasRestored) return;
    try {
      const raw = window.localStorage.getItem(storageKey);
      const parsed = raw ? (JSON.parse(raw) as StoredProgress | null) : null;
      // Revised banks restart at the opening instead of an old positional index.
      if (
        quizData.meta.revision &&
        parsed?.revision !== quizData.meta.revision
      ) {
        window.localStorage.removeItem(storageKey);
        window.localStorage.removeItem(completionKey);
        window.localStorage.removeItem(scoreKey);
        window.localStorage.removeItem(mistakesKey);
        setHasRestored(true);
        return;
      }
      if (reviewMissed) {
        const rawMistakes = window.localStorage.getItem(mistakesKey);
        const mistakeIds = rawMistakes
          ? (JSON.parse(rawMistakes) as unknown)
          : [];
        if (Array.isArray(mistakeIds)) {
          const missed = mcqQuestions.filter((question) =>
            mistakeIds.includes(question.id)
          );
          if (missed.length > 0) {
            setFixItQuestions(missed);
            setMode("fix-it");
            setCurrentIndex(0);
            setAnswers({});
            setCheckedIds({});
            setHasRestored(true);
            return;
          }
        }
      }

      if (!raw) {
        setHasRestored(true);
        return;
      }
      if (!parsed || parsed.version !== BUS_QUIZ_STORAGE_VERSION) {
        setHasRestored(true);
        return;
      }

      const boundedIndex = Math.min(
        Math.max(
          Number.isInteger(parsed.currentIndex) ? parsed.currentIndex : 0,
          0
        ),
        Math.max(mcqQuestions.length - 1, 0)
      );

      const restoredAnswers: AnswerMap = {};
      for (const [key, value] of Object.entries(parsed.answers ?? {})) {
        const id = Number(key);
        if (!questionIds.has(id)) continue;
        if (typeof value !== "number" || !Number.isInteger(value)) continue;
        const question = mcqQuestions.find((q) => q.id === id);
        if (!question) continue;
        if (value < 0 || value >= question.options.length) continue;
        restoredAnswers[id] = value;
      }

      const restoredChecked: Record<number, boolean> = {};
      for (const id of Array.isArray(parsed.checkedIds)
        ? parsed.checkedIds
        : []) {
        if (questionIds.has(id)) restoredChecked[id] = true;
      }
      for (const id of Object.keys(restoredAnswers)) {
        restoredChecked[Number(id)] = true;
      }

      setCurrentIndex(boundedIndex);
      setAnswers(restoredAnswers);
      setCheckedIds(restoredChecked);
      setHasRestored(true);
    } catch (error) {
      console.warn("Failed to restore bus quiz progress", error);
      setHasRestored(true);
    }
  }, [
    quizData,
    hasRestored,
    mcqQuestions,
    mistakesKey,
    questionIds,
    reviewMissed,
    storageKey,
    completionKey,
    scoreKey,
  ]);

  useEffect(() => {
    if (!isComplete || activeQuestions.length === 0) return;
    if (!startedRef.current) return;

    const score = activeQuestions.reduce(
      (correct, question) =>
        question.correct === answers[question.id] ? correct + 1 : correct,
      0
    );
    const wrongIds = activeQuestions
      .filter((question) => question.correct !== answers[question.id])
      .map((question) => question.id);

    trackQuizEvent("quiz_completed", {
      ...analyticsContext(),
      score,
      total: activeQuestions.length,
      questions_answered: Object.keys(checkedIds).length,
      elapsed_seconds: attemptStartedAtRef.current
        ? Math.round((Date.now() - attemptStartedAtRef.current) / 1000)
        : undefined,
      wrongIds,
    });

    try {
      if (wrongIds.length > 0) {
        window.localStorage.setItem(mistakesKey, JSON.stringify(wrongIds));
      } else {
        window.localStorage.removeItem(mistakesKey);
      }
    } catch {
      // localStorage may be unavailable.
    }
  }, [
    activeQuestions,
    analyticsContext,
    answers,
    checkedIds,
    isComplete,
    mistakesKey,
  ]);

  useEffect(() => {
    if (!quizData || !hasRestored || mode !== "full") return;
    const correctCount = mcqQuestions.reduce((count, q) => {
      if (checkedIds[q.id] && answers[q.id] === q.correct) return count + 1;
      return count;
    }, 0);
    const payload: StoredProgress = {
      version: BUS_QUIZ_STORAGE_VERSION,
      ...(quizData.meta.revision && { revision: quizData.meta.revision }),
      ...(quizData.meta.persistenceRevision && {
        persistenceRevision: quizData.meta.persistenceRevision,
      }),
      currentIndex,
      totalQuestions: mcqQuestions.length,
      answers: Object.fromEntries(
        Object.entries(answers).filter(([, value]) => typeof value === "number")
      ) as Record<number, number>,
      checkedIds: Object.keys(checkedIds)
        .filter((id) => checkedIds[Number(id)])
        .map((id) => Number(id)),
      correctCount,
    };
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(payload));
    } catch (error) {
      console.warn("Failed to save bus quiz progress", error);
    }
  }, [
    quizData,
    hasRestored,
    answers,
    checkedIds,
    currentIndex,
    storageKey,
    mode,
    mcqQuestions,
  ]);

  const resetQuiz = () => {
    if (startedRef.current) {
      trackQuizEvent("quiz_restarted", {
        ...analyticsContext(),
        questions_answered: Object.keys(checkedIdsRef.current).length,
        total: activeQuestions.length,
        elapsed_seconds: attemptStartedAtRef.current
          ? Math.round((Date.now() - attemptStartedAtRef.current) / 1000)
          : undefined,
      });
    }
    attemptIdRef.current = createQuizAttemptId();
    attemptStartedAtRef.current = null;
    startedRef.current = false;
    completedRef.current = false;
    abandonedRef.current = false;
    trackQuizEvent("quiz_viewed", {
      quizId,
      attemptId: attemptIdRef.current,
      surface: "standalone",
      mode: "full",
      total: mcqQuestions.length,
      ...studyContext,
    });
    setCurrentIndex(0);
    setAnswers({});
    setCheckedIds({});
    setIsComplete(false);
    setMode("full");
    setFixItQuestions([]);
    try {
      window.localStorage.removeItem(storageKey);
      window.localStorage.removeItem(completionKey);
      window.localStorage.removeItem(scoreKey);
      window.localStorage.removeItem(mistakesKey);
    } catch (error) {
      console.warn("Failed to clear saved progress", error);
    }
  };

  const totalQuestions = activeQuestions.length;
  const question = activeQuestions[currentIndex];
  const questionId = question?.id;
  const selected = questionId !== undefined ? answers[questionId] : undefined;
  const isChecked =
    questionId !== undefined ? checkedIds[questionId] ?? false : false;
  const isCorrectAnswer =
    isChecked && questionId !== undefined && selected === question?.correct;
  const progressPercent =
    totalQuestions > 0
      ? Math.round((Math.max(currentIndex + 1, 0) / totalQuestions) * 100)
      : 0;
  const progressStop = Math.min(Math.max(progressPercent, 0), 100);
  const notesTopic = quizData?.meta.title.replace(/\s+quiz$/i, "").trim();
  const notesLabel =
    notesLabelOverride ??
    (notesTopic
      ? formatQuizRunner(copy.notes, { topic: notesTopic })
      : copy.concept);

  useEffect(() => {
    if (
      !isChecked ||
      !question ||
      isComplete ||
      typeof IntersectionObserver === "undefined"
    ) {
      return;
    }

    const feedbackKey = `${attemptIdRef.current}:${question.id}`;
    if (feedbackViewedKeysRef.current.has(feedbackKey)) return;

    const targets = [
      desktopFeedbackRef.current,
      mobileFeedbackRef.current,
    ].filter((target): target is HTMLDivElement => target !== null);
    if (targets.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (feedbackViewedKeysRef.current.has(feedbackKey)) return;
        if (
          !entries.some(
            (entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5
          )
        ) {
          return;
        }

        feedbackViewedKeysRef.current.add(feedbackKey);
        trackQuizEvent("quiz_feedback_viewed", {
          ...analyticsContext(),
          total: totalQuestions,
          questions_answered: Object.keys(checkedIdsRef.current).length,
          question_id: question.id,
          question_number: currentIndex + 1,
          answer_correct: isCorrectAnswer,
          elapsed_seconds: attemptStartedAtRef.current
            ? Math.round((Date.now() - attemptStartedAtRef.current) / 1000)
            : 0,
        });
        observer.disconnect();
      },
      { threshold: 0.5 }
    );

    targets.forEach((target) => observer.observe(target));
    return () => observer.disconnect();
  }, [
    analyticsContext,
    currentIndex,
    isChecked,
    isComplete,
    isCorrectAnswer,
    question,
    totalQuestions,
  ]);

  if (isLoading) {
    return (
      <div className="rounded-2xl border border-indigo-100 bg-white p-6 shadow-sm">
        <div className="animate-pulse space-y-3">
          <div className="h-4 w-1/2 rounded bg-slate-200" />
          <div className="h-6 w-4/5 rounded bg-slate-200" />
          <div className="h-4 w-full rounded bg-slate-200" />
        </div>
      </div>
    );
  }

  if (!quizData || loadError) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-800">
        {loadError ?? formatQuizRunner(copy.notFound, { quizId })}
      </div>
    );
  }

  if (mcqQuestions.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-700 shadow-sm">
        {copy.empty}
      </div>
    );
  }

  if (isComplete) {
    const score = activeQuestions.reduce((correct, q) => {
      return q.correct === answers[q.id] ? correct + 1 : correct;
    }, 0);

    const missedQuestions =
      mode === "full"
        ? mcqQuestions.filter(
            (question) => question.correct !== answers[question.id]
          )
        : [];
    const nextFixItCount = Math.min(missedQuestions.length, 5);
    const hasReviewMissed = mode === "full" && nextFixItCount > 0;

    const startFixItRound = () => {
      if (missedQuestions.length === 0) return;
      const nextSet = missedQuestions.slice(0, 5);
      const shuffled =
        nextSet.length >= 3 ? shuffleQuestions(nextSet) : nextSet;
      attemptIdRef.current = createQuizAttemptId();
      attemptStartedAtRef.current = null;
      startedRef.current = false;
      completedRef.current = false;
      abandonedRef.current = false;
      trackQuizEvent("quiz_viewed", {
        quizId,
        attemptId: attemptIdRef.current,
        surface: "standalone",
        mode: "fix-it",
        total: shuffled.length,
        ...studyContext,
      });
      setFixItQuestions(shuffled);
      setMode("fix-it");
      setCurrentIndex(0);
      setAnswers({});
      setCheckedIds({});
      setIsComplete(false);
    };

    return (
      <div className="rounded-2xl border border-indigo-100 bg-white p-6 text-center shadow-sm">
        <Award className="mx-auto text-yellow-500" size={24} />
        <p className="mt-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          {copy.score}
        </p>
        <p className="mt-1 text-5xl font-extrabold tabular-nums text-slate-900 sm:text-6xl">
          {score}/{totalQuestions}
        </p>
        <div className="mx-auto mt-6 flex w-full max-w-xs flex-col gap-2">
          {hasReviewMissed ? (
            <button
              type="button"
              onClick={startFixItRound}
              className="inline-flex w-full items-center justify-center rounded-full bg-indigo-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
            >
              {formatQuizRunner(copy.missed, { count: nextFixItCount })}
            </button>
          ) : null}
          {notesHref ? (
            <a
              href={notesHref}
              onClick={() =>
                trackQuizNotesReviewClick({
                  quizId,
                  score,
                  total: totalQuestions,
                  mode,
                  studyContext,
                })
              }
              className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-5 py-2 text-sm font-semibold text-indigo-700 transition hover:border-indigo-300 hover:bg-indigo-100"
            >
              <BookOpen className="h-4 w-4" />
              {quizLinkLabel(notesLabel, locale, notesHref)}
            </a>
          ) : null}
          <a
            href={hubHref}
            className={`inline-flex w-full items-center justify-center rounded-full px-5 py-2 text-sm font-semibold transition ${
              hasReviewMissed
                ? "border border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                : "bg-slate-900 text-white hover:bg-slate-800"
            }`}
          >
            {quizLinkLabel(returnLabel ?? copy.backTopics, locale, hubHref)}
          </a>
          <div className="my-2 border-t border-slate-100" />
          <QuizWhatsAppCta
            locale={locale}
            quizId={quizId}
            quizTitle={quizData.meta.title}
            score={score}
            total={totalQuestions}
          />
          <ShareQuizResult
            locale={locale}
            quizTitle={quizData.meta.title}
            score={score}
            total={totalQuestions}
          />
          <button
            type="button"
            onClick={resetQuiz}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
          >
            <RotateCcw className="h-4 w-4" />
            {copy.restart}
          </button>
        </div>
      </div>
    );
  }

  const handleSelect = (optionIndex: number) => {
    if (isChecked || questionId === undefined) return;
    markStarted();
    trackQuizEvent("quiz_answered", {
      ...analyticsContext(),
      total: activeQuestions.length,
      questions_answered: Object.keys(checkedIdsRef.current).length + 1,
      question_number: currentIndex + 1,
      answer_correct: optionIndex === question.correct,
      elapsed_seconds: attemptStartedAtRef.current
        ? Math.round((Date.now() - attemptStartedAtRef.current) / 1000)
        : 0,
    });
    setAnswers((prev) => ({ ...prev, [questionId]: optionIndex }));
    setCheckedIds((prev) => ({ ...prev, [questionId]: true }));
  };

  const handleNext = () => {
    if (!isChecked || !question) return;
    trackQuizEvent("quiz_next_clicked", {
      ...analyticsContext(),
      total: totalQuestions,
      questions_answered: Object.keys(checkedIdsRef.current).length,
      question_id: question.id,
      question_number: currentIndex + 1,
      answer_correct: isCorrectAnswer,
      elapsed_seconds: attemptStartedAtRef.current
        ? Math.round((Date.now() - attemptStartedAtRef.current) / 1000)
        : 0,
    });
    if (currentIndex + 1 >= totalQuestions) {
      if (mode === "full") {
        try {
          window.localStorage.setItem(completionKey, new Date().toISOString());
          const finalScore = activeQuestions.reduce(
            (c, q) => (q.correct === answers[q.id] ? c + 1 : c),
            0
          );
          window.localStorage.setItem(
            scoreKey,
            JSON.stringify({ score: finalScore, total: totalQuestions })
          );
        } catch (error) {
          console.warn("Failed to store completion state", error);
        }
      }
      setIsComplete(true);
      return;
    }
    setCurrentIndex((prev) => prev + 1);
  };

  const feedbackCard = (
    <div
      className={`rounded-3xl border p-4 shadow-lg ${
        isCorrectAnswer
          ? "border-green-200 bg-green-50 text-green-900"
          : "border-red-200 bg-red-50 text-red-900"
      }`}
    >
      <p className="text-sm font-semibold">
        {isCorrectAnswer ? copy.correct : copy.incorrect}
      </p>
      <div className="mt-2 max-h-[40vh] overflow-auto text-sm leading-relaxed">
        {renderInlineMath(
          question.explanation,
          `bus-explanation-${question.id}`
        )}
      </div>
      <div className="mt-4">
        <button
          type="button"
          onClick={handleNext}
          className="w-full rounded-full bg-slate-900 py-3 text-sm font-bold text-white transition hover:bg-slate-800"
        >
          {currentIndex + 1 >= totalQuestions ? copy.finish : copy.next}
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center gap-3">
          <a
            href={hubHref}
            aria-label={quizLinkLabel(
              returnLabel ?? copy.backQuizzes,
              locale,
              hubHref
            )}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800"
          >
            <X className="h-4 w-4" />
          </a>
          <div className="flex-1">
            <div
              className="relative flex h-9 w-full items-center justify-end overflow-hidden rounded-full px-3"
              style={{
                background: `linear-gradient(to right, #6366f1 0%, #6366f1 ${progressStop}%, #f1f5f9 ${progressStop}%, #f1f5f9 100%)`,
              }}
            >
              {timeRemaining !== null ? (
                <span
                  className={`pointer-events-none relative z-10 mr-auto flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums shadow-sm ring-1 ${
                    timeRemaining <= 60
                      ? "bg-red-50 text-red-700 ring-red-200"
                      : "bg-white/90 text-slate-800 ring-slate-200/70"
                  }`}
                >
                  <Clock className="h-3 w-3" />
                  {formatTime(timeRemaining)}
                </span>
              ) : null}
              <span className="pointer-events-none relative z-10 ml-auto rounded-full bg-white/90 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-slate-800 shadow-sm ring-1 ring-slate-200/70">
                {currentIndex + 1}/{totalQuestions}
              </span>
            </div>
          </div>
        </div>
        <h2 className="mt-3 text-lg font-semibold text-slate-900">
          {renderInlineMath(question.question, `bus-question-${question.id}`)}
        </h2>
        <div className="mt-4 space-y-3">
          {getQuizOptionOrder(quizId, question).map(
            ({ option, originalIndex }, displayIndex) => {
              const isSelected = selected === originalIndex;
              const isCorrect = originalIndex === question.correct;
              const showCorrect = isChecked && isCorrect;
              const showWrong = isChecked && isSelected && !isCorrect;

              return (
                <button
                  key={`${question.id}-${originalIndex}`}
                  type="button"
                  onClick={() => handleSelect(originalIndex)}
                  disabled={isChecked}
                  className={`flex w-full items-center gap-3 rounded-2xl border-2 px-4 py-4 text-left text-sm font-semibold transition ${
                    isSelected
                      ? "border-indigo-500 bg-indigo-50"
                      : "border-slate-200 bg-white hover:border-indigo-200"
                  } ${
                    isChecked && isCorrect
                      ? "border-green-500 bg-green-50"
                      : isChecked && isSelected
                      ? "border-red-500 bg-red-50"
                      : ""
                  }`}
                >
                  <span
                    className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold text-white ${
                      isChecked && isCorrect
                        ? "bg-green-600"
                        : isChecked && isSelected
                        ? "bg-red-600"
                        : "bg-slate-900"
                    }`}
                  >
                    {String.fromCharCode(65 + displayIndex)}
                  </span>
                  <span
                    className={`flex-1 ${
                      isChecked && isCorrect
                        ? "text-green-900"
                        : isChecked && isSelected
                        ? "text-red-900"
                        : "text-slate-800"
                    }`}
                  >
                    {renderInlineMath(
                      option,
                      `bus-option-${question.id}-${originalIndex}`
                    )}
                  </span>
                  {showCorrect ? (
                    <CheckCircle className="text-green-600" size={20} />
                  ) : null}
                  {showWrong ? (
                    <XCircle className="text-red-600" size={20} />
                  ) : null}
                </button>
              );
            }
          )}
        </div>

        {isChecked ? (
          <div ref={desktopFeedbackRef} className="mt-4 hidden md:block">
            {feedbackCard}
          </div>
        ) : null}
      </section>

      {isChecked ? (
        <div className="fixed inset-x-0 bottom-0 z-30 md:hidden">
          <div
            ref={mobileFeedbackRef}
            className="mx-auto w-full max-w-3xl px-4 pb-[calc(env(safe-area-inset-bottom)+1rem)]"
          >
            {feedbackCard}
          </div>
        </div>
      ) : null}
    </div>
  );
}
